// Import directo del módulo (no del barrel `../map-engine`) — el barrel
// re-exporta `MapViewer.tsx`, que importa `maplibre-gl` a nivel de módulo
// y ejecuta un side-effect (`setWorkerUrl`) que revienta bajo jsdom en
// tests (`window.URL.createObjectURL` no implementado ahí). El template
// del visor ya renderiza su propio mapa por su cuenta — este archivo solo
// necesita el servicio de estilos, nunca el componente React del mapa.
import { mapStyleService } from '../map-engine/MapStyleService';

export interface AnalysisMeta {
  analysisId: string;
  lineName: string;
}

export interface ProgrammingBridgeState {
  config: Record<string, unknown> | null;
  isDefault: boolean;
  revision: number;
  frozenDataVersion: string | null;
  routeShapeFingerprint: string | null;
  lastEditedBy: { id: string; fullName: string } | null;
  lastEditedAt: string | null;
  canWrite: boolean;
}

const LINE_SEPARATOR = ' ';
const PARAGRAPH_SEPARATOR = ' ';

/**
 * `JSON.stringify` crudo dentro de un `<script>` inline es inseguro: un
 * `</script>` literal en los datos cierra el tag antes de tiempo, y
 * U+2028/U+2029 rompen el parseo de JS en algunos motores. Escapa lo
 * mínimo indispensable sin cambiar el valor decodificado.
 *
 * @remarks
 * Los separadores se referencian vía las constantes de arriba, nunca
 * como carácter literal dentro de un regex/string en este archivo — un
 * U+2028/U+2029 literal en el código fuente actúa como terminador de
 * línea para el propio parser de TypeScript y rompe la compilación.
 */
function safeJsonForInlineScript(value: unknown): string {
  return JSON.stringify(value)
    .split('<')
    .join('\\u003c')
    .split('>')
    .join('\\u003e')
    .split('&')
    .join('\\u0026')
    .split(LINE_SEPARATOR)
    .join('\\u2028')
    .split(PARAGRAPH_SEPARATOR)
    .join('\\u2029');
}

/**
 * Réplica exacta, en el navegador, del mecanismo de
 * `frozen-data/linea500/viewer/splice-final-visor-v3.js` — reemplaza los
 * mismos placeholders con los mismos datos, solo que obtenidos por fetch
 * autenticado en vez de leídos de disco en build time. El template nunca
 * se reescribe; esto es el "adaptador mínimo posible".
 *
 * `programmingState`/`channelId` son los dos placeholders para la
 * persistencia de PROGRAMACIÓN: el estado inicial (config vigente o
 * default, metadata de última edición, `canWrite`) y el nonce del canal
 * `postMessage` de esta instancia del iframe.
 *
 * `analysisMeta` (`analysisId`/`lineName`) es un placeholder aparte, no
 * parte de `data` — el nombre visible de la línea es una decisión del
 * portal (`analyses.ts`), no un dato congelado del análisis. Mantenerlo
 * fuera de `data` evita tener que tocar el bundle congelado de Línea 500
 * para generalizar el template a otras líneas.
 *
 * `/*__MAPTILER_RASTER_TILE_URL__*\/` — el template HTML no tiene acceso a
 * `import.meta.env` ni puede importar `mapStyleService` (es HTML/JS plano,
 * sin bundler), así que la key de MapTiler entra únicamente acá, del mismo
 * servicio singleton que ya la gestiona en toda la app — nunca una segunda
 * configuración ni la key hardcodeada en el template. Se inyecta solo la
 * URL de tiles raster (`mapStyleService.getRasterTileUrl('dark')`), nunca
 * un style.json vectorial completo — el template arma el source/layer
 * raster (`type: 'raster'`) con esa URL, igual que hacía antes con CARTO.
 */
/**
 * CSS-only — oculta todo el chrome del visor legacy salvo el módulo
 * PROGRAMACIÓN (marca, tabs, mapa, inspector, toolbar) para embeberlo como
 * un widget dentro de LineOperationsPage sin que se note "otra app". No
 * toca una sola línea del motor `Eng.*` ni de su JS: es una hoja de estilo
 * adicional, el mismo documento, los mismos IDs. `#map` no se hace
 * `display:none` — MapLibre necesita un container con tamaño real al
 * construirse o falla su inicialización — se lo reduce a 1x1 y se lo saca
 * de pantalla en su lugar.
 */
const ISOLATE_PROGRAMACION_CSS = `<style>
  #map { position: absolute !important; top: -9999px !important; left: -9999px !important; right: auto !important; bottom: auto !important; width: 1px !important; height: 1px !important; pointer-events: none !important; }
  #toast, #audit-banner, #left-panel-header, .seg-control, #toolbar, #layers-btn, #layers-panel, #tech-toggle, #inspector, #methodology-popover { display: none !important; }
  #left-panel { position: static !important; width: 100% !important; height: 100% !important; max-height: none !important; box-shadow: none !important; border: none !important; background: transparent !important; backdrop-filter: none !important; border-radius: 0 !important; }
  #module-content { padding: 2px 2px 10px !important; }
  .module-pane { display: none !important; }
  .module-pane[data-module="programacion"] { display: block !important; }
</style>
`;

export function spliceVisorTemplate(
  template: string,
  data: unknown,
  auditData: unknown,
  programmingState: ProgrammingBridgeState,
  channelId: string,
  analysisMeta: AnalysisMeta,
  options?: { isolateModule?: 'programacion' }
): string {
  let out = template
    .replace('/*__DATA__*/', safeJsonForInlineScript(data))
    .replace('/*__AUDIT_DATA__*/', safeJsonForInlineScript(auditData))
    .replace('/*__PROGRAMMING_STATE__*/', safeJsonForInlineScript(programmingState))
    .replace('/*__PROGRAMMING_CHANNEL_ID__*/', safeJsonForInlineScript(channelId))
    .replace('/*__ANALYSIS_META__*/', safeJsonForInlineScript(analysisMeta))
    .replace(
      '/*__MAPTILER_RASTER_TILE_URL__*/',
      safeJsonForInlineScript(mapStyleService.getRasterTileUrl('dark'))
    );

  if (
    out.includes('__DATA__') ||
    out.includes('__AUDIT_DATA__') ||
    out.includes('__PROGRAMMING_STATE__') ||
    out.includes('__PROGRAMMING_CHANNEL_ID__') ||
    out.includes('__ANALYSIS_META__') ||
    out.includes('__MAPTILER_RASTER_TILE_URL__')
  ) {
    throw new Error('Placeholder del visor no reemplazado.');
  }

  if (options?.isolateModule === 'programacion') {
    if (!out.includes('<div id="map"></div>')) {
      throw new Error('Ancla de aislamiento del visor no encontrada.');
    }
    out = out.replace('<div id="map"></div>', ISOLATE_PROGRAMACION_CSS + '<div id="map"></div>');
  }

  return out;
}
